import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tosApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';

export default function TosAcceptance() {
  const { user, logout, refreshUser } = useAuth();
  const navigate                      = useNavigate();
  const scrollRef                     = useRef(null);

  const [tos,           setTos]           = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [accepting,     setAccepting]     = useState(false);
  const [error,         setError]         = useState('');
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [checked1,      setChecked1]      = useState(false);
  const [checked2,      setChecked2]      = useState(false);

  useEffect(() => {
    tosApi.getCurrent()
      .then(r => setTos(r.data.data))
      .catch(() => setError('Failed to load Terms of Service. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  // Track scroll — enable accept button only after scrolling to bottom
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setScrolledToEnd(true);
  };

  const canAccept = scrolledToEnd && checked1 && checked2;

  const handleAccept = async () => {
    if (!canAccept) return;
    setAccepting(true); setError('');
    try {
      await tosApi.accept();
      await refreshUser();           // reload user with tosAccepted: true
      // Redirect to dashboard
      const dest = {
        SUPER_ADMIN: '/dashboard/super-admin',
        ADMIN:       '/dashboard/admin',
        EMPLOYEE:    '/dashboard/employee',
      };
      navigate(dest[user?.role] || '/dashboard/employee', { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to record acceptance. Please try again.');
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (loading) return (
    <div style={S.page}>
      <Spinner size={32} />
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.shell}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.logoRow}>
            <div style={S.logoMark}>K</div>
            <span style={S.logoText}>Tracern</span>
          </div>
          <div>
            <h1 style={S.title}>Terms of Service</h1>
            <p style={S.subtitle}>
              Version {tos?.version} · Effective {tos?.effectiveDate} ·
              Please read carefully before continuing
            </p>
          </div>
        </div>

        {/* Mandatory scroll area */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={S.scrollArea}
        >
          {tos?.sections?.map((section, i) => (
            <div key={i} style={S.section}>
              <h3 style={S.sectionTitle}>{section.title}</h3>
              <p style={S.sectionBody}>{section.body}</p>
            </div>
          ))}
          <div style={{ height: '40px' }} /> {/* scroll target */}
        </div>

        {/* Scroll hint */}
        {!scrolledToEnd && (
          <div style={S.scrollHint}>
            ↓ Scroll to the bottom to continue
          </div>
        )}

        {/* Acceptance checklist */}
        <div style={{ ...S.checklist, opacity: scrolledToEnd ? 1 : 0.4, pointerEvents: scrolledToEnd ? 'auto' : 'none' }}>
          <label style={S.checkRow}>
            <input
              type="checkbox"
              checked={checked1}
              onChange={e => setChecked1(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={S.checkLabel}>
              I have read and understood the Terms of Service and agree to be bound by them.
            </span>
          </label>
          <label style={S.checkRow}>
            <input
              type="checkbox"
              checked={checked2}
              onChange={e => setChecked2(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={S.checkLabel}>
              I confirm that I am authorised to accept these terms on behalf of my organisation.
            </span>
          </label>
        </div>

        {error && (
          <div style={S.errorBox}>{error}</div>
        )}

        {/* Action buttons */}
        <div style={S.actions}>
          <button onClick={handleDecline} style={S.declineBtn}>
            Decline & Sign Out
          </button>
          <button
            onClick={handleAccept}
            disabled={!canAccept || accepting}
            style={{
              ...S.acceptBtn,
              opacity: canAccept ? 1 : 0.5,
              cursor: canAccept ? 'pointer' : 'not-allowed',
            }}
          >
            {accepting ? 'Recording acceptance…' : 'I Accept — Continue to Tracern'}
          </button>
        </div>

        <p style={S.legalNote}>
          Your acceptance is recorded with your IP address and timestamp for legal compliance.
          Tracern v{tos?.version} · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center', background: 'var(--color-bg)',
    padding: '24px 16px',
  },
  shell: {
    width: '100%', maxWidth: '760px',
    background: 'var(--color-surface)', borderRadius: '16px',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '24px 28px',
    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%)',
    color: '#fff',
  },
  logoRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  logoMark: {
    width: '32px', height: '32px', borderRadius: '8px',
    background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', fontWeight: 700, color: '#fff',
  },
  logoText:  { fontSize: '18px', fontWeight: 700, color: '#fff' },
  title:     { fontSize: '22px', fontWeight: 700, marginBottom: '4px' },
  subtitle:  { fontSize: '13px', opacity: 0.85 },
  scrollArea: {
    height: '380px', overflowY: 'auto', padding: '24px 28px',
    borderBottom: '1px solid var(--color-border)',
  },
  section:     { marginBottom: '24px' },
  sectionTitle:{ fontSize: '15px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '8px' },
  sectionBody: { fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.7 },
  scrollHint: {
    textAlign: 'center', padding: '10px', fontSize: '13px',
    color: 'var(--color-text-muted)', background: 'var(--color-bg)',
    borderBottom: '1px solid var(--color-border)',
  },
  checklist: {
    padding: '20px 28px',
    display: 'flex', flexDirection: 'column', gap: '14px',
    borderBottom: '1px solid var(--color-border)',
    transition: 'opacity 0.3s',
  },
  checkRow:  { display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' },
  checkLabel:{ fontSize: '14px', color: 'var(--color-text)', lineHeight: 1.5 },
  errorBox:  {
    margin: '0 28px', padding: '10px 14px',
    background: '#FFF5F5', border: '1px solid #FEB2B2',
    borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--color-danger)',
  },
  actions: {
    display: 'flex', gap: '10px', padding: '20px 28px',
    justifyContent: 'flex-end', flexWrap: 'wrap',
  },
  declineBtn: {
    padding: '10px 18px', borderRadius: 'var(--radius-md)',
    background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-border)', cursor: 'pointer',
    fontSize: '14px',
  },
  acceptBtn: {
    padding: '10px 24px', borderRadius: 'var(--radius-md)',
    background: 'var(--color-primary)', color: '#fff',
    border: 'none', fontSize: '14px', fontWeight: 600,
    transition: 'opacity 0.2s',
  },
  legalNote: {
    textAlign: 'center', fontSize: '11px', color: 'var(--color-text-muted)',
    padding: '0 28px 16px',
  },
};
